import { Grid, Col } from "@components/layout/Grid"
import { cn, tv, VariantProps } from "@utils/tw"
import { getQuarters } from "@utils/transportAffordabilityHelpers"
import { defineBlock } from "eddev/blocks"
import { useHydrating } from "eddev/routing"
import {
  ComponentPropsWithoutRef,
  CSSProperties,
  Fragment,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { ChartConfiguration, Chart as ChartJS, ChartOptions, ChartTypeRegistry, registerables } from "chart.js/auto"
import { Chart } from "react-chartjs-2"
import { ALL_FUELS, ICE, BEV, PHEV, HYBRID, HFCEV, INFO } from "@components/atoms/Icons"
import { numberWithCommas } from "@utils/numberWithCommas"
import { useIsMobile } from "@hooks/useViewportDerived"
import { useMeasure } from "react-use"
import { useEvData, useGlossaryTerms } from "@hooks/queries"
import { colors } from "@utils/evDataHelpers"
import { ContainerReset } from "@components/layout/GridReset"
import {
  FloatingFilter,
  RollingQuarters,
  TickDate,
  Timeline,
  TimelineSelection,
  states,
} from "@components/atoms/data/Components"
import { VSpace } from "@components/layout/Spacer"
import { Button } from "@components/atoms/Button"
import { AnimatePresence, useInView } from "framer-motion"
import { calculateAngle } from "@utils/chartjsUtils"
import { Tooltip } from "react-tooltip"
import ChartDataLabels from "chartjs-plugin-datalabels"
import ExportChartLink from "@utils/chartExport"

export const meta: BlockMeta = {
  title: "EV Index Header",
  category: "common",
  icon: "dashboard",
  keywords: ["ev index"],
  tags: ["root"],
  editMode: "preview",
  conditions: {
    postTypes: ["page", "post", "data-embed"],
    multiple: false,
  },
}

ChartJS.register(...registerables)

type DataDetailT = {
  sales: number
  quarter: string
}

type ChartDataT = {
  state: string
  vehicleType: string
  manufacturer: string
  model: string
  fuelType: "ICE" | "PHEV" | "BEV" | "Hybrid" | "HFCEV"
  data: DataDetailT[]
}

type SalesByManufacturerT = {
  manufacturer: string
  sales: number
}

type LabelDataT = {
  percentage: string
  color: string
}

export default defineBlock("data/evindex-header", (props) => {
  const hydrating = useHydrating()
  const glossary = useGlossaryTerms(undefined).data?.siteSettings?.themeSiteSettings?.glossary
  const [currentPeriod, setCurrentPeriod] = useState<number>(0)
  const [quarters, setQuarters] = useState<string[] | null>(null)

  const getLatestYearWithData = (quarters: string[] | null) => {
    if (!quarters) return null
    // Extract unique years from quarters
    let yearsWithData = [...new Set(quarters.map((q) => q.split(" ")[1]))]

    // Ensure we only have valid numbers
    yearsWithData = yearsWithData.filter((year) => !isNaN(parseInt(year)))

    // Sort years in descending order (latest first)
    yearsWithData.sort((a, b) => parseInt(b) - parseInt(a))

    // Get the current year
    const thisYear = new Date().getFullYear()

    // If the current year has data, return it; otherwise, return the latest available year
    return yearsWithData.includes(thisYear.toString()) ? thisYear : parseInt(yearsWithData[0])
  }

  // Set initial state with the latest year that has data
  const [currentYear, setCurrentYear] = useState<number | null>(() => getLatestYearWithData(quarters))
  const chartRef = useRef<ChartJS>(null)
  const isMobile = useIsMobile()
  const [makersList, setMakersList] = useState<SalesByManufacturerT[] | null>(null)
  const [currentFilter, setCurrentFilter] = useState<keyof typeof colors>("All_fuels")
  const [instanceRef, instanceSpecs] = useMeasure<HTMLDivElement>()
  const evIndexData = useEvData(undefined)
  const [stateSelector, setStateSelector] = useState<keyof typeof states>("all")
  const [rolling, setRolling] = useState<"normal" | "rolling" | "ytd">("normal")

  // Animation state
  const [midpointValue, setMidpointValue] = useState<number>(0)
  const [previousMidpointValue, setPreviousMidpointValue] = useState<number>(0)
  const [targetMidpointValue, setTargetMidpointValue] = useState<number>(0)
  const [animationFrame, setAnimationFrame] = useState<number | null>(null)

  // Easing function for ease-out
  const easeOut = (t: number) => 1 - Math.pow(1 - t, 3)

  // Animate the needle
  function animateNeedle(previousValue: number, targetValue: number) {
    let start: number | null = null

    // Cancel any.. ongoing animation frame
    if (animationFrame) {
      cancelAnimationFrame(animationFrame)
    }

    const duration = 500 // 1 second animation duration

    // The animation step function
    const step = (timestamp: number) => {
      if (!start) start = timestamp
      const progress = (timestamp - start) / duration

      // Apply easing
      const easedProgress = easeOut(Math.min(progress, 1))

      // Calculate the new value
      const newValue = previousValue + (targetValue - previousValue) * easedProgress

      // Update midpointValue for drawing
      setMidpointValue(newValue)

      // Continue the animation or stop if complete
      if (progress < 1) {
        setAnimationFrame(requestAnimationFrame(step))
      } else {
        setPreviousMidpointValue(targetValue)
        setMidpointValue(targetValue) // Ensure it ends exactly at the target value
      }
    }

    // Start the animation
    setAnimationFrame(requestAnimationFrame(step))
  }

  // Effect to animate toward the target midpoint value
  useEffect(() => {
    if (targetMidpointValue !== previousMidpointValue) {
      animateNeedle(previousMidpointValue, targetMidpointValue)
    }
  }, [targetMidpointValue])

  // Update midpoint value
  const updateMidpointValue = (newValue: number) => {
    setPreviousMidpointValue(midpointValue) // Store the current value before updating
    setTargetMidpointValue(newValue) // Set the target value to trigger the animation
  }

  const rawChartData: ChartDataT[] = useMemo(() => {
    const sortedData: ChartDataT[] = []

    if (evIndexData.data?.evIndexData) {
      const datasets = evIndexData.data.evIndexData.map((data) => JSON.parse(data?.data ?? ""))
      const sortedQuarters = getQuarters(datasets)
      setQuarters(sortedQuarters)
      setCurrentYear(getLatestYearWithData(sortedQuarters))

      setCurrentPeriod(sortedQuarters.length - 1)

      datasets.forEach((data) => {
        if (sortedQuarters)
          sortedData.push({
            state: data["STATE"],
            vehicleType: data["VEHICLE TYPE"],
            manufacturer: data["MANUFACTURER"],
            model: data["MODEL"],
            fuelType: data["FUEL TYPE"],
            data: sortedQuarters.map((quarter) => ({
              quarter: quarter,
              sales: parseInt(data[quarter]),
            })),
          })
      })
    }
    return sortedData
  }, [evIndexData.data?.evIndexData])

  // Chart data logic
  const chartData = useMemo(() => {
    if (rawChartData && quarters && currentYear) {
      const ice = rawChartData
        .filter((a) => a.fuelType === "ICE")
        .filter((a) => {
          if (stateSelector === "all") {
            return a
          } else if (a.state === states[stateSelector]) {
            return a
          }
        })
      const bev = rawChartData
        .filter((a) => a.fuelType === "BEV")
        .filter((a) => {
          if (stateSelector === "all") {
            return a
          } else if (a.state === states[stateSelector]) {
            return a
          }
        })
      const phev = rawChartData
        .filter((a) => a.fuelType === "PHEV")
        .filter((a) => {
          if (stateSelector === "all") {
            return a
          } else if (a.state === states[stateSelector]) {
            return a
          }
        })
      const hybrid = rawChartData
        .filter((a) => a.fuelType === "Hybrid")

        .filter((a) => {
          if (stateSelector === "all") {
            return a
          } else if (a.state === states[stateSelector]) {
            return a
          }
        })
      const hfcev = rawChartData
        .filter((a) => a.fuelType === "HFCEV")
        .filter((a) => {
          if (stateSelector === "all") {
            return a
          } else if (a.state === states[stateSelector]) {
            return a
          }
        })

      // Function to group and sort by manufacturer and total sales in the currentPeriod
      const groupAndSortByManufacturer = (
        data: ChartDataT[],
        currentPeriod: number,
      ): { manufacturer: string; data: ChartDataT[]; totalSales: number }[] => {
        // Group by manufacturer
        const groupedByManufacturer: { [key: string]: { data: ChartDataT[]; totalSales: number } } = data.reduce(
          (acc, item) => {
            const sales = item.data[currentPeriod]?.sales || 0
            if (!acc[item.manufacturer]) {
              acc[item.manufacturer] = { data: [], totalSales: 0 }
            }
            acc[item.manufacturer].data.push(item)
            acc[item.manufacturer].totalSales += sales
            return acc
          },
          {} as { [key: string]: { data: ChartDataT[]; totalSales: number } },
        )

        // Convert the grouped object to an array of manufacturers
        return Object.keys(groupedByManufacturer)
          .map((manufacturer) => ({
            manufacturer,
            data: groupedByManufacturer[manufacturer].data,
            totalSales: groupedByManufacturer[manufacturer].totalSales,
          }))
          .sort((a, b) => b.totalSales - a.totalSales) // Sort manufacturers by total sales in descending order
      }

      let all: {
        type: string
        data: ChartDataT[]
        count: number
        color: string
        brand: {
          manufacturer: string
          data: ChartDataT[]
          totalSales: number
        }[]
      }[] = []

      if (rolling === "rolling") {
        const mergeBrands = (brandArrays: { manufacturer: string; data: ChartDataT[]; totalSales: number }[][]) => {
          return brandArrays.flat().reduce(
            (acc, brand) => {
              const existingBrand = acc.find((b) => b.manufacturer === brand.manufacturer)
              if (existingBrand) {
                existingBrand.totalSales += brand.totalSales
                existingBrand.data = existingBrand.data.concat(brand.data)
              } else {
                acc.push(brand)
              }
              return acc
            },
            [] as { manufacturer: string; data: ChartDataT[]; totalSales: number }[],
          )
        }

        const iceBrands = mergeBrands([
          groupAndSortByManufacturer(ice, currentPeriod),
          groupAndSortByManufacturer(ice, currentPeriod - 1),
          groupAndSortByManufacturer(ice, currentPeriod - 2),
          groupAndSortByManufacturer(ice, currentPeriod - 3),
        ])

        const bevBrands = mergeBrands([
          groupAndSortByManufacturer(bev, currentPeriod),
          groupAndSortByManufacturer(bev, currentPeriod - 1),
          groupAndSortByManufacturer(bev, currentPeriod - 2),
          groupAndSortByManufacturer(bev, currentPeriod - 3),
        ])

        const phevBrands = mergeBrands([
          groupAndSortByManufacturer(phev, currentPeriod),
          groupAndSortByManufacturer(phev, currentPeriod - 1),
          groupAndSortByManufacturer(phev, currentPeriod - 2),
          groupAndSortByManufacturer(phev, currentPeriod - 3),
        ])

        const hybridBrands = mergeBrands([
          groupAndSortByManufacturer(hybrid, currentPeriod),
          groupAndSortByManufacturer(hybrid, currentPeriod - 1),
          groupAndSortByManufacturer(hybrid, currentPeriod - 2),
          groupAndSortByManufacturer(hybrid, currentPeriod - 3),
        ])

        const hfcevBrands = mergeBrands([
          groupAndSortByManufacturer(hfcev, currentPeriod),
          groupAndSortByManufacturer(hfcev, currentPeriod - 1),
          groupAndSortByManufacturer(hfcev, currentPeriod - 2),
          groupAndSortByManufacturer(hfcev, currentPeriod - 3),
        ])

        all = [
          {
            type: "ICE",
            data: ice,
            count: iceBrands.reduce((a, b) => a + b.totalSales, 0),
            color: colors["ICE"],
            brand: iceBrands,
          },
          {
            type: "BEV",
            data: bev,
            count: bevBrands.reduce((a, b) => a + b.totalSales, 0),
            color: colors["BEV"],
            brand: bevBrands,
          },
          {
            type: "PHEV",
            data: phev,
            count: phevBrands.reduce((a, b) => a + b.totalSales, 0),
            color: colors["PHEV"],
            brand: phevBrands,
          },
          {
            type: "Hybrid",
            data: hybrid,
            count: hybridBrands.reduce((a, b) => a + b.totalSales, 0),
            color: colors["Hybrid"],
            brand: hybridBrands,
          },
          {
            type: "HFCEV",
            data: hfcev,
            count: hfcevBrands.reduce((a, b) => a + b.totalSales, 0),
            color: colors["HFCEV"],
            brand: hfcevBrands,
          },
        ]
      } else if (rolling === "normal") {
        all = [
          {
            type: "ICE",
            data: ice,
            count: ice.reduce((a: number, b: ChartDataT) => a + b.data[currentPeriod]?.sales, 0),
            color: colors["ICE"],
            brand: groupAndSortByManufacturer(ice, currentPeriod),
          },
          {
            type: "BEV",
            data: bev,
            count: bev.reduce((a: number, b: ChartDataT) => a + b.data[currentPeriod]?.sales, 0),
            color: colors["BEV"],
            brand: groupAndSortByManufacturer(bev, currentPeriod),
          },
          {
            type: "PHEV",
            data: phev,
            count: phev.reduce((a: number, b: ChartDataT) => a + b.data[currentPeriod]?.sales, 0),
            color: colors["PHEV"],
            brand: groupAndSortByManufacturer(phev, currentPeriod),
          },
          {
            type: "Hybrid",
            data: hybrid,
            count: hybrid.reduce((a: number, b: ChartDataT) => a + b.data[currentPeriod]?.sales, 0),
            color: colors["Hybrid"],
            brand: groupAndSortByManufacturer(hybrid, currentPeriod),
          },
          {
            type: "HFCEV",
            data: hfcev,
            count: hfcev.reduce((a: number, b: ChartDataT) => a + b.data[currentPeriod]?.sales, 0),
            color: colors["HFCEV"],
            brand: groupAndSortByManufacturer(hfcev, currentPeriod),
          },
        ]
      } else {
        // Work out YTD (Year to Date)

        // Extract year from quarter string
        const getYear = (quarter: string) => quarter.split(" ")[1] // "Q3 2024" → "2024"

        // Filter data to only include the current year's quarters
        const filterByYear = (data: ChartDataT[]) =>
          data
            .map((item) => ({
              ...item,
              data: item.data.filter((d) => getYear(d.quarter) === currentYear.toString()),
            }))
            .filter((item) => item.data.length > 0) // Remove entries with no data

        const iceYTD = filterByYear(ice)
        const bevYTD = filterByYear(bev)
        const phevYTD = filterByYear(phev)
        const hybridYTD = filterByYear(hybrid)
        const hfcevYTD = filterByYear(hfcev)

        // Function to sum up sales for each fuel type
        const sumSales = (data: ChartDataT[]) =>
          data.reduce((total, entry) => total + entry.data.reduce((sum, q) => sum + q.sales, 0), 0)

        // Function to group and sort manufacturers
        const groupAndSortByManufacturerYTD = (data: ChartDataT[]) => {
          const groupedByManufacturer: { [key: string]: { data: ChartDataT[]; totalSales: number } } = {}

          data.forEach((item) => {
            const totalSales = item.data.reduce((sum, q) => sum + q.sales, 0)
            if (!groupedByManufacturer[item.manufacturer]) {
              groupedByManufacturer[item.manufacturer] = { data: [], totalSales: 0 }
            }
            groupedByManufacturer[item.manufacturer].data.push(item)
            groupedByManufacturer[item.manufacturer].totalSales += totalSales
          })

          return Object.keys(groupedByManufacturer)
            .map((manufacturer) => ({
              manufacturer,
              data: groupedByManufacturer[manufacturer].data,
              totalSales: groupedByManufacturer[manufacturer].totalSales,
            }))
            .sort((a, b) => b.totalSales - a.totalSales)
        }

        // Create YTD dataset
        all = [
          {
            type: "ICE",
            data: iceYTD,
            count: sumSales(iceYTD),
            color: colors["ICE"],
            brand: groupAndSortByManufacturerYTD(iceYTD),
          },
          {
            type: "BEV",
            data: bevYTD,
            count: sumSales(bevYTD),
            color: colors["BEV"],
            brand: groupAndSortByManufacturerYTD(bevYTD),
          },
          {
            type: "PHEV",
            data: phevYTD,
            count: sumSales(phevYTD),
            color: colors["PHEV"],
            brand: groupAndSortByManufacturerYTD(phevYTD),
          },
          {
            type: "Hybrid",
            data: hybridYTD,
            count: sumSales(hybridYTD),
            color: colors["Hybrid"],
            brand: groupAndSortByManufacturerYTD(hybridYTD),
          },
          {
            type: "HFCEV",
            data: hfcevYTD,
            count: sumSales(hfcevYTD),
            color: colors["HFCEV"],
            brand: groupAndSortByManufacturerYTD(hfcevYTD),
          },
        ]
      }

      const sorted = all.sort((a, b) => a.count - b.count)
      let filtered = sorted
      if (currentFilter !== "All_fuels") {
        filtered = sorted.filter((data) => data.type === currentFilter)
      }
      const total = filtered.reduce((a, b) => a + b.count, 0)

      // Sales my manufacturer
      const map = new Map()
      const manufacturerData = rawChartData.filter((a) => {
        if (stateSelector === "all") {
          return a
        } else if (a.state === states[stateSelector]) {
          return a
        }
      })

      manufacturerData.forEach((data) => {
        const manufacturer = data.manufacturer
        let sales = 0
        if (rolling === "rolling") {
          sales =
            data.data[currentPeriod].sales +
            data.data[currentPeriod - 1].sales +
            data.data[currentPeriod - 2].sales +
            data.data[currentPeriod - 3].sales
        } else if (rolling === "normal") {
          sales = data.data[currentPeriod].sales
        } else {
          const getYearFromQuarter = (quarter: string): string | null => {
            const match = quarter.match(/\b\d{4}\b/) // Extracts the 4-digit year
            return match ? match[0] : null
          }
          sales = data.data
            .filter((entry) => getYearFromQuarter(entry.quarter) === currentYear.toString())
            .reduce((sum, entry) => sum + (entry.sales || 0), 0)
        }

        if (currentFilter === "All_fuels" || currentFilter === data.fuelType) {
          const el = map.get(manufacturer)
          if (el) {
            el.sales += sales
          } else {
            if (sales > 0)
              map.set(manufacturer, {
                manufacturer,
                sales,
              })
          }
        }
      })

      setMakersList(
        Array.from(map, ([manufacturer, data]) => ({
          manufacturer: manufacturer,
          sales: data.sales,
        })).sort((a, b) => b.sales - a.sales),
      )

      return {
        labels: filtered.map((data) => data.type),
        datasets: [
          {
            label: "By manufacturer",
            data: filtered
              .filter((data) => data.count > 0)
              .map((data) => data.brand.map((brand) => brand.totalSales))
              .flat()
              .concat((225 / 360) * total),
            backgroundColor: filtered
              .filter((data) => data.count > 0)
              .map((data) => data.brand.map((brand) => data.color).join("!!!!"))
              .join("!!!!")
              .split("!!!!")
              .concat("#222222"),
            borderWidth: 1,
            borderColor: "#222222",
            weight: 4,
          },
          {
            label: "By fuel type",
            data: filtered
              .filter((data) => data.count > 0)
              .map((data) => data.count)
              .concat((225 / 360) * total),
            backgroundColor: filtered
              .filter((data) => data.count > 0)
              .map((data) => data.color)
              .concat("#222222"),
            borderWidth: 1,
            borderColor: "#222222",
            weight: 1,
          },
        ],
      }
    }

    return null
  }, [currentPeriod, rawChartData, currentFilter, stateSelector, rolling, currentYear])

  // Chart options for Chart.js
  const chartOptions: ChartOptions<"doughnut"> = {
    responsive: true,
    aspectRatio: 1 / 0.65,
    maintainAspectRatio: false,
    cutout: "60%",
    rotation: -112.5,
    circumference: 360,

    events: [],

    animation: {
      onProgress: ChartDraw,
      onComplete: ChartDraw,
    },

    plugins: {
      legend: {
        display: false,
      },

      datalabels: {
        formatter: (value, context) => {
          if (context.datasetIndex === 0) return null
          if (currentFilter !== "All_fuels") return null
          // if (stateSelector !== "all") return null
          const dataset = context.dataset.data

          if (value === dataset[dataset.length - 1]) return null
          const total = dataset.slice(0, -1).reduce((acc, val) => (acc as number) + (val as number), 0)
          const percentage = total ? ((value / (total as number)) * 100).toFixed(2) + "%" : ""
          return (value / (total as number)) * 100 < 2 ? null : percentage
        },
        //@ts-ignore
        color: (context) => context.dataset.backgroundColor[context.dataIndex],
        font: {
          family: "Manrope",
          weight: "bold",
          size: 12,
        },
        //@ts-ignore
        backgroundColor: "#222",
        //@ts-ignore
        borderColor: (context) => context.dataset.backgroundColor[context.dataIndex],
        borderWidth: 1,
        borderRadius: 18,
        padding: 5,
        anchor: "start",
        align: "start",
        offset: -10,
        display: true,
      },
    },
  }

  function ChartDraw() {
    if (chartRef.current && chartData?.datasets[0]) {
      var canvas = chartRef.current.canvas
      var ctx = canvas.getContext("2d")
      var cw = canvas.offsetWidth
      var ch = canvas.offsetHeight
      var cx = cw / 2
      var cy = ch / 2

      if (currentFilter !== "All_fuels") {
        const zero = (-202.5 * Math.PI) / 180 // Zero point in radians
        const maxValue = chartData.datasets[0].data.reduce((a, b) => a + b, 0) // Maximum value in the dataset
        const rangeRadians = (360 * Math.PI) / 180 // Range of the chart in radian
        const angleInRadians = calculateAngle(midpointValue, zero, maxValue, rangeRadians)

        if (ctx) {
          ctx.translate(cx, cy)
          ctx.rotate(angleInRadians)
          ctx.beginPath()
          ctx.moveTo(0, -8)
          ctx.lineTo(cx - 20, 0)
          ctx.lineTo(0, 8)
          ctx.fillStyle = "#fff"
          ctx.fill()
          ctx.rotate(-angleInRadians)
          ctx.translate(-cx, -cy)
          ctx.beginPath()
          ctx.arc(cx, cy, 7, 0, Math.PI * 2)
          ctx.fill()

          ctx.fillStyle = "#222"
          ctx.beginPath()
          ctx.arc(cx, cy, cx / 2, 0, (360 * Math.PI) / 180)
          ctx.fill()
        }
      } else {
        // Draw %
        const dataset = chartData.datasets.filter((data) => data.label === "By fuel type")[0]
        const total = dataset.data.slice(0, -1).reduce((acc, val) => (acc as number) + (val as number), 0)
        const labelData: LabelDataT[] = []

        if (dataset.data.length === 5) {
          labelData.push({
            color: "rgb(151, 229, 245)",
            percentage: "0.00%",
          })
        }

        dataset.data.forEach((value, i) => {
          if ((value / (total as number)) * 100 < 2)
            labelData.push({
              color: dataset.backgroundColor[i],
              percentage: ((value / (total as number)) * 100).toFixed(2) + "%",
            })
        })

        if (labelData.length > 0 && ctx) {
          requestAnimationFrame(() => {
            labelData.forEach((label, i) => {
              drawLabelBox(
                canvas,
                ctx as CanvasRenderingContext2D,
                label.percentage,
                "#222",
                label.color,
                i,
                labelData.length,
              )
            })
          })
        }
      }

      if (ctx) {
        ctx.save()
        ctx.translate(cx + 5, cy - 7)
        ctx.font = "bold 32px Manrope"
        ctx.fillStyle = "#fff"
        ctx.textAlign = "center"
        ctx.textBaseline = "middle"
        ctx.letterSpacing = "0em"
        ctx.fillText(`${currentFilter.replace("_", " ")}`, 0, 0)
        ctx.translate(-(cx + 5), -(cy - 7))

        ctx.translate(cx, cy - 50)
        ctx.font = "bold 10px Manrope"
        ctx.fillStyle = "#fff"
        ctx.textAlign = "center"
        ctx.textBaseline = "middle"
        ctx.letterSpacing = ".12em"
        ctx.fillText(`ALL NEW LIGHT VEHICLE SALES`, 0, 0)
        ctx.translate(-cx, -(cy - 50))
        ctx.restore()
      }
    }
  }
  const [filterOpen, setFilterOpen] = useState<boolean>(false)
  const blockRef = useRef<HTMLDivElement>(null)
  const inView = useInView(blockRef, { amount: 0.5 })

  function clearStyles() {
    const parent = document.querySelector("ol")
    if (parent) {
      parent.classList.remove("faded")
      Object.entries(parent.children).forEach(([_, el]) => {
        el.classList.remove("selected")
      })
    }
  }

  return (
    <HeaderContainer ref={blockRef}>
      {env.admin && (
        <ExportChartLink
          chartRef={chartRef}
          fileName="chart.png"
          scale={10}
          drawManual={(canvas, ctx) => ChartDrawManual(canvas, ctx, chartRef.current!.data, currentFilter)}
        >
          Download Chart as PNG
        </ExportChartLink>
      )}

      <div className="grid-auto px-4 md:px-6 xxl:px-0 !w-[100vw] xl:!max-w-[1600px] xxl:!max-w-[1720px] gap-6">
        {/* Filter List */}
        <div className="col-span-12 md:col-span-8 md:col-start-3 lg:col-start-1">
          <FilterList>
            <li>
              <FilterButton
                type="all"
                selected={currentFilter === "All_fuels"}
                onClick={() => {
                  setCurrentFilter("All_fuels")
                  updateMidpointValue(0)
                  clearStyles()
                }}
              >
                <i>{ALL_FUELS}</i>
                <span>All fuels</span>
              </FilterButton>
            </li>
            <li>
              <FilterButton
                type="ice"
                selected={currentFilter === "All_fuels" || currentFilter === "ICE" ? true : false}
                onClick={() => {
                  setCurrentFilter("ICE")
                  setMidpointValue(0)
                  clearStyles()
                }}
              >
                <i>{ICE}</i>
                <span>ICE</span>
              </FilterButton>
            </li>
            <li>
              <FilterButton
                type="bev"
                selected={currentFilter === "All_fuels" || currentFilter === "BEV" ? true : false}
                onClick={() => {
                  setCurrentFilter("BEV")
                  setMidpointValue(0)
                  clearStyles()
                }}
              >
                <i>{BEV}</i>
                <span>BEV</span>
              </FilterButton>
            </li>
            <li>
              <FilterButton
                type="phev"
                selected={currentFilter === "All_fuels" || currentFilter === "PHEV" ? true : false}
                onClick={() => {
                  setCurrentFilter("PHEV")
                  setMidpointValue(0)
                  clearStyles()
                }}
              >
                <i>{PHEV}</i>
                <span>PHEV</span>
              </FilterButton>
            </li>
            <li>
              <FilterButton
                type="hybrid"
                selected={currentFilter === "All_fuels" || currentFilter === "Hybrid" ? true : false}
                onClick={() => {
                  setCurrentFilter("Hybrid")
                  setMidpointValue(0)
                  clearStyles()
                }}
              >
                <i>{HYBRID}</i>
                <span>Hybrid</span>
              </FilterButton>
            </li>
            <li>
              <FilterButton
                type="hfcev"
                selected={currentFilter === "All_fuels" || currentFilter === "HFCEV" ? true : false}
                onClick={() => {
                  setCurrentFilter("HFCEV")
                  setMidpointValue(0)
                }}
              >
                <i>{HFCEV}</i>
                <span>HFCEV</span>
              </FilterButton>
            </li>
          </FilterList>
          <VSpace initial={6} />
        </div>

        {/* Chart Area */}
        <div className="col-span-12 md:col-span-10 md:col-start-2 lg:col-span-8 lg:col-start-1 lg:row-start-2">
          {!hydrating && chartData && (
            <div
              ref={instanceRef}
              className="relative h-[var(--evi-chart-height)] overflow-hidden"
              style={
                {
                  "--evi-chart-height": instanceSpecs ? `${instanceSpecs.width * 0.5}px` : "0px",
                  "--evi-chart-height-md": instanceSpecs ? `${instanceSpecs.width * 0.5}px` : "0px",
                } as CSSProperties
              }
            >
              <div
                className="absolute top-0 left-0 w-full [&_canvas]:!w-full [&_canvas]:!h-auto"
                style={{ height: `${instanceSpecs.width * 0.7}px` }}
              >
                <Chart
                  type="doughnut"
                  // @ts-ignore
                  ref={chartRef}
                  data={chartData}
                  options={chartOptions}
                  //@ts-ignore
                  plugins={[ChartDataLabels]}
                />
              </div>
            </div>
          )}
        </div>

        {/* Manufacturer List */}
        <div className="col-span-12 lg:col-span-4 lg:row-span-2">
          {makersList && chartData && (
            <>
              <FilteredInfo selected={currentFilter}>
                <i></i> <span>{currentFilter.replace("_", " ")}</span>{" "}
                {currentFilter !== "All_fuels" && glossary && (
                  <>
                    <a className={`tt-${currentFilter}`}>{INFO}</a>
                    <Tooltip
                      anchorSelect={`.tt-${currentFilter}`}
                      style={{ width: "250px", zIndex: 50, backgroundColor: "#ffffff", color: "#000000" }}
                    >
                      {glossary.filter((term) => term?.term === currentFilter)[0]?.description}
                    </Tooltip>
                  </>
                )}{" "}
                <em>
                  (
                  {numberWithCommas(
                    chartData.datasets[1].data.reduce((a, b) => a + b, 0) -
                      chartData.datasets[1].data[chartData.datasets[1].data.length - 1],
                  )}{" "}
                  total sales)
                </em>
              </FilteredInfo>
              <ManufacturerList>
                {makersList.map((data, i) => (
                  <li
                    key={i}
                    onMouseOver={(e) => {
                      if (currentFilter !== "All_fuels") {
                        // Calculate the cumulative total of sales up to and including this manufacturer
                        const cumulativeSales = chartData.datasets[0].data.slice(0, i + 1).reduce((a, b) => a + b, 0)

                        // Calculate the previous cumulative total before this manufacturer
                        const previousSales =
                          i === 0 ? 0 : chartData.datasets[0].data.slice(0, i).reduce((a, b) => a + b, 0)

                        // Calculate the midpoint value within the segment
                        const midValue = previousSales + (cumulativeSales - previousSales) / 2

                        updateMidpointValue(midValue)

                        e.currentTarget.parentElement?.classList.add("faded")
                        Object.entries(e.currentTarget.parentElement!.children).forEach(([_, el]) => {
                          el.classList.remove("selected")
                        })
                        e.currentTarget.classList.add("selected")
                      }
                    }}
                  >
                    {data.manufacturer}
                  </li>
                ))}
              </ManufacturerList>
            </>
          )}
        </div>

        {isMobile && !hydrating && quarters && (
          <AnimatePresence>
            {inView && (
              <FloatingFilter open={filterOpen} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <Button
                  size="tiny"
                  color="yellow"
                  icon={filterOpen ? "CARET_DOWN" : "CARET_UP"}
                  iconStyle="right"
                  onClick={() => setFilterOpen(!filterOpen)}
                >
                  Filter this data
                </Button>
                <StateSelector className="pr-0 !w-full [&>div]:!rounded-[6px]">
                  <div>
                    <StateRow>
                      <StateSelection>
                        <label>
                          <input
                            type="radio"
                            name="states"
                            value="all"
                            defaultChecked
                            onChange={(e) => {
                              if (e.currentTarget.checked) setStateSelector("all")
                            }}
                          />
                          All states
                        </label>
                      </StateSelection>
                    </StateRow>
                    <StateRow>
                      {Object.entries(states).map(([i, state]) => {
                        if (i === "all") return <Fragment key={i}></Fragment>
                        return (
                          <StateSelection key={i}>
                            <label>
                              <input
                                type="radio"
                                name="states"
                                value={i}
                                onChange={(e) => {
                                  if (e.currentTarget.checked)
                                    setStateSelector(e.currentTarget.value as keyof typeof states)
                                }}
                              />
                              {i.toUpperCase()}
                            </label>
                          </StateSelection>
                        )
                      })}
                    </StateRow>
                  </div>
                </StateSelector>

                <RollingQuarters className="pr-0 !w-full [&_form]:w-full [&_form>div]:!rounded-[6px] after:hidden">
                  <form>
                    <div>
                      <StateRow>
                        <StateSelection>
                          <label>
                            <input
                              type="radio"
                              name="states"
                              value="quarterly"
                              defaultChecked
                              onChange={(e) => {
                                if (e.currentTarget.checked) {
                                  setCurrentPeriod(quarters.length - 1)
                                  setRolling("normal")
                                }
                              }}
                            />
                            Quarterly
                          </label>
                        </StateSelection>

                        <StateSelection>
                          <label>
                            <input
                              type="radio"
                              name="states"
                              value="rolling"
                              onChange={(e) => {
                                if (e.currentTarget.checked) {
                                  setCurrentPeriod(quarters.length - 1)
                                  setRolling("rolling")
                                }
                              }}
                            />
                            Rolling 4 Quarters
                          </label>
                        </StateSelection>

                        <StateSelection>
                          <label>
                            <input
                              type="radio"
                              name="states"
                              value="ytd"
                              onChange={(e) => {
                                if (e.currentTarget.checked) {
                                  setCurrentPeriod(quarters.length - 1)
                                  setRolling("ytd")
                                }
                              }}
                            />
                            Year to date
                          </label>
                        </StateSelection>
                      </StateRow>
                    </div>
                  </form>
                </RollingQuarters>

                {!hydrating && quarters && currentYear && (
                  <Timeline className="h-[40px] [&_input]:top-1/2">
                    <ul>
                      {(() => {
                        // Extract unique years from quarters
                        const years = [...new Set(quarters.map((q) => q.split(" ")[1]))]
                        years.sort((a, b) => parseInt(a) - parseInt(b))

                        return rolling === "ytd"
                          ? years.map((year, i) => (
                              <li key={i}>
                                <TickDate style={{ opacity: year === currentYear.toString() ? 1 : 0.5 }}>
                                  {year}
                                </TickDate>
                              </li>
                            ))
                          : quarters.map((period, i) => {
                              if (rolling === "rolling" && [0, 1, 2].includes(i)) return <Fragment key={i} />
                              return (
                                <li key={i}>
                                  <TickDate
                                    style={{
                                      opacity: i === currentPeriod ? 1 : i === 0 ? (currentPeriod === 1 ? 0 : 0.5) : 0,
                                    }}
                                  >
                                    {rolling === "rolling" ? (
                                      <>
                                        {quarters[i - 3]} - {period}
                                      </>
                                    ) : (
                                      period
                                    )}
                                  </TickDate>
                                </li>
                              )
                            })
                      })()}
                    </ul>

                    {rolling === "ytd" && (
                      <input
                        key="ytd"
                        type="range"
                        min={0}
                        max={(() => {
                          const years = [...new Set(quarters.map((q) => q.split(" ")[1]))]
                          years.sort((a, b) => parseInt(a) - parseInt(b))
                          return years.length - 1
                        })()}
                        step={1}
                        defaultValue={currentYear}
                        list="period-marks"
                        onChange={(e) => {
                          const years = [...new Set(quarters.map((q) => q.split(" ")[1]))]
                          years.sort((a, b) => parseInt(a) - parseInt(b))
                          setCurrentYear(parseInt(years[parseInt(e.target.value)]))
                        }}
                      />
                    )}
                    {rolling === "rolling" && (
                      <input
                        key="rolling"
                        type="range"
                        min={3}
                        max={quarters.length - 1}
                        step={1}
                        defaultValue={quarters.length - 1}
                        list="period-marks"
                        onChange={(e) => {
                          setCurrentPeriod(parseInt(e.target.value))
                        }}
                      />
                    )}

                    {rolling === "normal" && (
                      <input
                        key="normal"
                        type="range"
                        min={0}
                        max={quarters.length - 1}
                        step={1}
                        defaultValue={quarters.length - 1}
                        list="period-marks"
                        onChange={(e) => {
                          setCurrentPeriod(parseInt(e.target.value))
                        }}
                      />
                    )}
                  </Timeline>
                )}
              </FloatingFilter>
            )}
          </AnimatePresence>
        )}
        {!isMobile && (
          <div className="col-span-12">
            <TimelineSelection>
              <StateSelector>
                <div>
                  <StateRow>
                    <StateSelection>
                      <label>
                        <input
                          type="radio"
                          name="states"
                          value="all"
                          defaultChecked
                          onChange={(e) => {
                            if (e.currentTarget.checked) setStateSelector("all")
                          }}
                        />
                        All states
                      </label>
                    </StateSelection>
                  </StateRow>
                  <StateRow>
                    {Object.entries(states).map(([i, state]) => {
                      if (i === "all") return <Fragment key={i}></Fragment>
                      return (
                        <StateSelection key={i}>
                          <label>
                            <input
                              type="radio"
                              name="states"
                              value={i}
                              onChange={(e) => {
                                if (e.currentTarget.checked)
                                  setStateSelector(e.currentTarget.value as keyof typeof states)
                              }}
                            />
                            {i.toUpperCase()}
                          </label>
                        </StateSelection>
                      )
                    })}
                  </StateRow>
                </div>
              </StateSelector>

              {!hydrating && quarters && currentYear && (
                <RollingQuarters>
                  <form>
                    <div>
                      <StateRow>
                        <StateSelection>
                          <label>Time selection</label>
                        </StateSelection>
                      </StateRow>

                      <StateRow>
                        <StateSelection>
                          <label>
                            <input
                              type="radio"
                              name="states"
                              value="quarterly"
                              defaultChecked
                              onChange={(e) => {
                                if (e.currentTarget.checked) {
                                  setCurrentPeriod(quarters.length - 1)
                                  setRolling("normal")
                                }
                              }}
                            />
                            Quarterly
                          </label>
                        </StateSelection>

                        <StateSelection>
                          <label>
                            <input
                              type="radio"
                              name="states"
                              value="rolling"
                              onChange={(e) => {
                                if (e.currentTarget.checked) {
                                  setCurrentPeriod(quarters.length - 1)
                                  setRolling("rolling")
                                }
                              }}
                            />
                            Rolling 4 Quarters
                          </label>
                        </StateSelection>

                        <StateSelection>
                          <label>
                            <input
                              type="radio"
                              name="states"
                              value="ytd"
                              onChange={(e) => {
                                if (e.currentTarget.checked) {
                                  setCurrentPeriod(quarters.length - 1)
                                  setRolling("ytd")
                                }
                              }}
                            />
                            Year to date
                          </label>
                        </StateSelection>
                      </StateRow>
                    </div>
                  </form>
                </RollingQuarters>
              )}

              {!hydrating && quarters && currentYear && (
                <Timeline>
                  <ul>
                    {(() => {
                      // Extract unique years from quarters
                      const years = [...new Set(quarters.map((q) => q.split(" ")[1]))]
                      years.sort((a, b) => parseInt(a) - parseInt(b))

                      return rolling === "ytd"
                        ? years.map((year, i) => (
                            <li key={i}>
                              <TickDate style={{ opacity: year === currentYear.toString() ? 1 : 0.5 }}>{year}</TickDate>
                            </li>
                          ))
                        : quarters.map((period, i) => {
                            if (rolling === "rolling" && [0, 1, 2].includes(i)) return <Fragment key={i} />
                            return (
                              <li key={i}>
                                <TickDate
                                  style={{
                                    opacity: i === currentPeriod ? 1 : i === 0 ? (currentPeriod === 1 ? 0 : 0.5) : 0,
                                  }}
                                >
                                  {rolling === "rolling" ? (
                                    <>
                                      {quarters[i - 3]} - {period}
                                    </>
                                  ) : (
                                    period
                                  )}
                                </TickDate>
                              </li>
                            )
                          })
                    })()}
                  </ul>

                  {rolling === "ytd" && (
                    <input
                      key="ytd"
                      type="range"
                      min={0}
                      max={(() => {
                        const years = [...new Set(quarters.map((q) => q.split(" ")[1]))]
                        years.sort((a, b) => parseInt(a) - parseInt(b))
                        return years.length - 1
                      })()}
                      step={1}
                      defaultValue={currentYear}
                      list="period-marks"
                      onChange={(e) => {
                        const years = [...new Set(quarters.map((q) => q.split(" ")[1]))]
                        years.sort((a, b) => parseInt(a) - parseInt(b))
                        setCurrentYear(parseInt(years[parseInt(e.target.value)]))
                      }}
                    />
                  )}
                  {rolling === "rolling" && (
                    <input
                      key="rolling"
                      type="range"
                      min={3}
                      max={quarters.length - 1}
                      step={1}
                      defaultValue={quarters.length - 1}
                      list="period-marks"
                      onChange={(e) => {
                        setCurrentPeriod(parseInt(e.target.value))
                      }}
                    />
                  )}

                  {rolling === "normal" && (
                    <input
                      key="normal"
                      type="range"
                      min={0}
                      max={quarters.length - 1}
                      step={1}
                      defaultValue={quarters.length - 1}
                      list="period-marks"
                      onChange={(e) => {
                        setCurrentPeriod(parseInt(e.target.value))
                      }}
                    />
                  )}
                </Timeline>
              )}
            </TimelineSelection>
          </div>
        )}
      </div>
    </HeaderContainer>
  )
})

const HeaderContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "bg-black py-6 w-full text-white",
      "[&_.--grid>div]:flex [&_.--grid>div]:flex-col [&_.--grid>div]:items-center [&_.--grid>div]:justify-center",
      className,
    )}
    {...props}
  />
))
HeaderContainer.displayName = "HeaderContainer"

// Background colors below are ported 1:1 from utils/evDataHelpers' `colors`
// map (same rationale already documented in components/atoms/data/
// Components.tsx's FuelTypeFilter/SortedTable): ICE and Hybrid happen to
// exactly match this project's mid-grey / alt-yellow-light tokens, so those
// use named color classes; the rest have no token equivalent and stay as
// literal rgb() arbitrary values. The `_50` variants are the same colors at
// 50% alpha — `colors["X_50"]` is a literal rgba() with a 0.5 alpha channel,
// not a Tailwind `/50` opacity modifier — so they're kept as literal rgba()
// arbitrary values to guarantee a byte-for-byte match.
const filteredInfoStyles = tv({
  base: [
    "flex items-center text-white px-6 pb-4 w-full",
    "[&_i]:w-[20px] [&_i]:h-[20px] [&_i]:rounded-[20px] [&_i]:inline-block [&_i]:ml-3",
    "[&_a]:inline-flex [&_a]:mr-3",
    "[&_span]:type-title-s [&_span]:inline-block [&_span]:px-2",
    "[&_em]:not-italic [&_em]:type-label-s [&_em]:inline-block [&_em]:mt-[4px]",
  ],
  variants: {
    selected: {
      All_fuels: "[&_i]:bg-white",
      ICE: "[&_i]:bg-mid-grey",
      BEV: "[&_i]:bg-[rgb(96,124,222)]",
      PHEV: "[&_i]:bg-[rgb(255,147,68)]",
      Hybrid: "[&_i]:bg-alt-yellow-light",
      HFCEV: "[&_i]:bg-[rgb(151,229,245)]",
      All_fuels_50: "[&_i]:bg-[rgba(255,255,255,0.5)]",
      ICE_50: "[&_i]:bg-[rgba(142,142,142,0.5)]",
      BEV_50: "[&_i]:bg-[rgba(96,124,222,0.5)]",
      PHEV_50: "[&_i]:bg-[rgba(255,147,68,0.5)]",
      Hybrid_50: "[&_i]:bg-[rgba(255,237,72,0.5)]",
      HFCEV_50: "[&_i]:bg-[rgba(151,229,245,0.5)]",
    },
  },
})

const FilteredInfo = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<"div"> & VariantProps<typeof filteredInfoStyles>
>(({ selected, className, ...props }, ref) => (
  <div ref={ref} className={cn(filteredInfoStyles({ selected }), className)} {...props} />
))
FilteredInfo.displayName = "FilteredInfo"

const ManufacturerList = forwardRef<HTMLOListElement, ComponentPropsWithoutRef<"ol">>(
  ({ className, ...props }, ref) => (
    <ol
      ref={ref}
      className={cn(
        "[counter-reset:item] columns-2 px-6 w-full",
        "[&_li]:type-label-s [&_li]:py-1 [&_li]:[counter-increment:item_1] [&_li]:transition-all [&_li]:duration-[350ms] [&_li]:ease-movement [&_li]:cursor-pointer",
        "[&_li]:before:content-[counter(item)] [&_li]:before:text-mid-grey [&_li]:before:inline-block [&_li]:before:w-[25px] [&_li]:before:text-right [&_li]:before:pr-2",
        "[&.faded_li]:opacity-30 [&.faded_li.selected]:opacity-100",
        "sm:columns-3",
        "lg:columns-2",
        className,
      )}
      {...props}
    />
  ),
)
ManufacturerList.displayName = "ManufacturerList"

const FilterList = forwardRef<HTMLUListElement, ComponentPropsWithoutRef<"ul">>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn(
      "flex gap-4 w-full mx-auto max-w-[400px]",
      "[&_li]:flex [&_li]:[align-items:top] [&_li]:justify-center [&_li]:w-[calc((100%-(var(--spacing-4)*5))*(1/6))]",
      "md:flex-wrap md:max-w-full md:[&_li]:w-[calc((100%-var(--spacing-4)-var(--spacing-4))*(1/3))]",
      "lg:[&_li]:w-auto lg:gap-6 lg:justify-center",
      className,
    )}
    {...props}
  />
))
FilterList.displayName = "FilterList"

const filterButtonStyles = tv({
  base: [
    "flex flex-col w-full gap-2 my-3 transition-all duration-[350ms] ease-movement appearance-none border-none bg-transparent opacity-20",
    "[&_i]:relative [&_i]:w-full [&_i]:h-0 [&_i]:pt-[100%] [&_i]:px-3 [&_i]:pb-0 [&_i]:rounded-[40px] [&_i]:overflow-hidden [&_i]:flex [&_i]:items-center [&_i]:text-black [&_i]:transition-all [&_i]:duration-[350ms] [&_i]:ease-movement",
    "[&_i_svg]:w-[calc(100%-var(--spacing-3)-var(--spacing-3))] [&_i_svg]:h-auto [&_i_svg]:absolute [&_i_svg]:top-1/2 [&_i_svg]:left-3 [&_i_svg]:-translate-y-1/2",
    "[&_span]:type-label-caps [&_span]:font-bold [&_span]:w-full [&_span]:text-center [&_span]:text-white",
    "hover:-translate-y-[var(--spacing-1)]",
    "md:w-[60px]",
  ],
  variants: {
    selected: {
      true: "opacity-100",
    },
    type: {
      all: "[&_i]:bg-white",
      ice: "[&_i]:bg-mid-grey",
      bev: "[&_i]:bg-[rgb(96,124,222)]",
      phev: "[&_i]:bg-[rgb(255,147,68)]",
      hybrid: "[&_i]:bg-alt-yellow-light",
      hfcev: "[&_i]:bg-[rgb(151,229,245)]",
    },
  },
})

const FilterButton = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<"button"> & VariantProps<typeof filterButtonStyles>
>(({ selected, type, className, ...props }, ref) => (
  <button ref={ref} className={cn(filterButtonStyles({ selected, type }), className)} {...props} />
))
FilterButton.displayName = "FilterButton"

const StateSelector = forwardRef<HTMLFormElement, ComponentPropsWithoutRef<"form">>(({ className, ...props }, ref) => (
  <form
    ref={ref}
    className={cn(
      "block pr-6 relative",
      "[&>div]:flex [&>div]:flex-col [&>div]:bg-dark-grey [&>div]:rounded-md [&>div]:border [&>div]:border-mid-grey [&>div]:overflow-hidden",
      className,
    )}
    {...props}
  />
))
StateSelector.displayName = "StateSelector"

const StateRow = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex first-of-type:border-b first-of-type:border-b-mid-grey", className)} {...props} />
))
StateRow.displayName = "StateRow"

const StateSelection = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex grow border-l border-l-mid-grey first-of-type:border-l-0",
      "[&_input]:hidden [&_label:has(input:checked)]:bg-yellow [&_label:has(input:checked)]:text-black",
      "[&_label]:type-label-s [&_label]:py-2 [&_label]:px-3 [&_label]:w-full [&_label]:text-center [&_label]:cursor-pointer [&_label]:whitespace-nowrap",
      className,
    )}
    {...props}
  />
))
StateSelection.displayName = "StateSelection"

function drawRoundedRect(
  ctx: CanvasRenderingContext2D,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  r: number,
  fillColor: string,
  strokeColor: string,
) {
  const w = x1 - x0
  const h = y1 - y0

  // Ensure border radius does not exceed the rectangle's dimensions
  r = Math.min(r, w / 2, h / 2)

  ctx.beginPath()
  ctx.moveTo(x1 - r, y0)
  ctx.quadraticCurveTo(x1, y0, x1, y0 + r)
  ctx.lineTo(x1, y1 - r)
  ctx.quadraticCurveTo(x1, y1, x1 - r, y1)
  ctx.lineTo(x0 + r, y1)
  ctx.quadraticCurveTo(x0, y1, x0, y1 - r)
  ctx.lineTo(x0, y0 + r)
  ctx.quadraticCurveTo(x0, y0, x0 + r, y0)
  ctx.closePath()

  // Fill background
  ctx.fillStyle = fillColor
  ctx.fill()

  // Draw border
  ctx.strokeStyle = strokeColor
  ctx.lineWidth = 1
  ctx.stroke()
}

function drawLabelBox(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  text: string,
  bgColor: string,
  borderColor: string,
  index: number,
  totalLabels: number,
): void {
  ctx.save()

  const width = canvas.offsetWidth
  const height = canvas.offsetHeight

  const padding = 3
  const fontSize = 12
  const borderRadius = 18
  const fontFamily = "Manrope"
  const fontWeight = "bold"

  // Set font
  ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`
  ctx.textAlign = "center"
  ctx.textBaseline = "middle"

  // Measure text width
  const textMetrics = ctx.measureText(text)
  const textWidth = textMetrics.width
  const textHeight = fontSize * 1.5 // Approximate height

  // Define pill dimensions
  const pillWidth = textWidth + padding * 4
  const pillHeight = textHeight + padding * 2

  // Ensure borderRadius does not exceed dimensions
  const r = Math.min(borderRadius, pillWidth / 2, pillHeight / 2)

  // Center horizontally and distribute with a gap
  const gap = 10 // Space between pills
  const totalWidth = totalLabels * pillWidth + (totalLabels - 1) * gap
  const startX = (width - totalWidth) / 2 // Center all pills
  const x = startX + index * (pillWidth + gap)
  const y = height * (2.1 / 3) - pillHeight - 20 // Bottom padding

  // Draw pill shape using `ellipse`
  drawRoundedRect(ctx, x, y, x + pillWidth, y + pillHeight, r, bgColor, borderColor)

  // Fill background
  ctx.fillStyle = bgColor
  ctx.fill()

  // Draw border
  ctx.lineWidth = 1
  ctx.strokeStyle = borderColor
  ctx.stroke()

  // Draw text
  ctx.fillStyle = borderColor
  ctx.fillText(text, x + pillWidth / 2, y + pillHeight / 2)

  ctx.restore()
}

function ChartDrawManual(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  data: ChartJS["data"],
  currentFilter: string,
) {
  const cw = canvas.width
  const ch = canvas.height
  const cx = cw / 2
  const cy = ch / 2

  const scale = cw / canvas.offsetWidth || 1 // calculate scaling if needed

  ctx.save()

  if (currentFilter !== "All_fuels") {
    const zero = (-202.5 * Math.PI) / 180
    const maxValue = data.datasets[0].data.reduce((a: any, b: any) => a + b, 0)
    const rangeRadians = (360 * Math.PI) / 180
    const midpointValue = 0 // TODO: You might need to pass `midpointValue` too
    const angleInRadians = calculateAngle(midpointValue, zero, maxValue, rangeRadians)

    ctx.translate(cx, cy)
    ctx.rotate(angleInRadians)
    ctx.beginPath()
    ctx.moveTo(0, -8 * scale)
    ctx.lineTo(cx - 20 * scale, 0)
    ctx.lineTo(0, 8 * scale)
    ctx.fillStyle = "#fff"
    ctx.fill()
    ctx.rotate(-angleInRadians)
    ctx.translate(-cx, -cy)

    ctx.beginPath()
    ctx.arc(cx, cy, 7 * scale, 0, Math.PI * 2)
    ctx.fill()
  } else {
    // draw label boxes (percentages < 2% etc.)
    const dataset = data.datasets.find((d) => d.label === "By fuel type")
    if (dataset) {
      const total = dataset.data.slice(0, -1).reduce((acc: any, val: any) => acc + val, 0)

      dataset.data.forEach((value, i) => {
        if (((value as number) / total) * 100 < 2) {
          drawLabelBox(
            canvas,
            ctx,
            `${(((value as number) / total) * 100).toFixed(2)}%`,
            "#222",
            Array.isArray(dataset.backgroundColor) ? (dataset.backgroundColor[i] as string) : "#fff",
            i,
            dataset.data.length,
          )
        }
      })
    }
  }

  ctx.restore()

  // draw center title
  ctx.save()
  ctx.translate(cx + 5 * scale, cy - 7 * scale)
  ctx.font = `bold ${32 * scale}px Manrope`
  ctx.fillStyle = "#fff"
  ctx.textAlign = "center"
  ctx.textBaseline = "middle"
  ctx.fillText(currentFilter.replace("_", " "), 0, 0)
  ctx.restore()

  ctx.save()
  ctx.translate(cx, cy - 50 * scale)
  ctx.font = `bold ${10 * scale}px Manrope`
  ctx.fillStyle = "#fff"
  ctx.textAlign = "center"
  ctx.textBaseline = "middle"
  ctx.fillText(`ALL NEW LIGHT VEHICLE SALES`, 0, 0)
  ctx.restore()
}
